// Area: metadata (owner i18n-metadata in wave 1, data-model-objects in wave 2).
// The metamodel CRUD: the whole workspace model, field types, apps, objects, fields, relations with
// their link fields, pipelines and stages, plus the translations of metadata labels (plan §8.10).
// Reads return labels in the member's language; `?raw=1` (permission `metadata.write`) returns
// them as written (plain or `{ en, de }`) for editors.
import { z } from 'zod';
import {
  App,
  CreateAppInput,
  CreateFieldInput,
  CreateLinkFieldInput,
  CreateObjectTypeInput,
  CreateOptionSetInput,
  CreatePipelineInput,
  CreateRelationInput,
  CreateStageInput,
  Field,
  FieldTypeInfo,
  LinkField,
  LocaleCode,
  ObjectDetail,
  ObjectType,
  ObjectTypeSummary,
  OptionSet,
  Pipeline,
  PutTranslationsInput,
  RawApp,
  RawField,
  RawObjectDetail,
  RawObjectType,
  RawOptionSet,
  RawPipeline,
  RawRelation,
  RawWorkspaceModel,
  Relation,
  ReorderInput,
  Stage,
  TranslationsReport,
  UpdateAppInput,
  UpdateFieldInput,
  UpdateLinkFieldInput,
  UpdateObjectTypeInput,
  UpdateOptionSetInput,
  UpdatePipelineInput,
  UpdateRelationInput,
  UpdateStageInput,
  WorkspaceModel,
} from '@dataroom/core';
import { defineContract } from '../define';
import { q } from '../query';

export { PutTranslationsInput, TranslationsReport };

const Key = z.object({ key: z.string().min(1).max(63) });
const KeyField = Key.extend({ fieldKey: z.string().min(1).max(63) });
const Id = z.object({ id: z.uuid() });
const IdLinkField = Id.extend({ linkFieldId: z.uuid() });
const IdStage = Id.extend({ stageId: z.uuid() });
/** `?raw=1`: texts as written (plain or localized) instead of the member's language. */
const RawQuery = z.object({ raw: q.bool().optional() });
const ObjectFilter = RawQuery.extend({ object: z.string().min(1).max(63).optional() });

// -- the model --------------------------------------------------------------------------------

export const getModelContract = defineContract({
  method: 'GET',
  path: '/model',
  query: RawQuery,
  response: z.union([WorkspaceModel, RawWorkspaceModel]),
  summary:
    "Whole workspace model: apps, objects, fields, relations, pipelines (labels in the member's language; ?raw=1 as written)",
});

export const listFieldTypesContract = defineContract({
  method: 'GET',
  path: '/field-types',
  response: z.array(FieldTypeInfo),
  summary: 'Registered field types with operators and config schema',
});

// -- translations -------------------------------------------------------------------------------

export const TranslationsQuery = z.object({
  /** Locale of the translations (`de`, `de-AT`, `fr`, …). */
  locale: LocaleCode,
  /** Only this object (api key); apps are left out then. */
  object: z.string().min(1).max(63).optional(),
});

export const getTranslationsContract = defineContract({
  method: 'GET',
  path: '/metadata/translations',
  query: TranslationsQuery,
  response: TranslationsReport,
  permission: 'metadata.write',
  summary:
    'Translatable labels of the data model with their text in one locale and missing counts per object',
});

export const putTranslationsContract = defineContract({
  method: 'PUT',
  path: '/metadata/translations',
  body: PutTranslationsInput,
  response: TranslationsReport,
  permission: 'metadata.write',
  summary:
    'Set or remove the texts of one locale (labels, descriptions, options, stages); returns its report',
});

// -- apps -----------------------------------------------------------------------------------------

export const listAppsContract = defineContract({
  method: 'GET',
  path: '/apps',
  query: RawQuery,
  response: z.union([z.array(App), z.array(RawApp)]),
  summary: 'Apps (navigation groups) in order',
});

export const createAppContract = defineContract({
  method: 'POST',
  path: '/apps',
  body: CreateAppInput,
  status: 201,
  response: App,
  summary: 'Create an app',
});

export const updateAppContract = defineContract({
  method: 'PATCH',
  path: '/apps/:id',
  params: Id,
  body: UpdateAppInput,
  response: App,
  summary: 'Rename, reorder or change the icon of an app',
});

export const deleteAppContract = defineContract({
  method: 'DELETE',
  path: '/apps/:id',
  params: Id,
  response: z.void(),
  summary: 'Delete an app (its objects move to "Other")',
});

// -- objects --------------------------------------------------------------------------------------

export const listObjectsContract = defineContract({
  method: 'GET',
  path: '/objects',
  query: RawQuery,
  response: z.union([z.array(ObjectTypeSummary), z.array(ObjectTypeSummary.extend(RawObjectType.shape))]),
  summary: 'Objects with record counts (estimates for large objects), field and relation counts',
});

export const createObjectContract = defineContract({
  method: 'POST',
  path: '/objects',
  body: CreateObjectTypeInput,
  status: 201,
  response: ObjectType,
  summary: 'Create an object with its title field',
});

export const getObjectContract = defineContract({
  method: 'GET',
  path: '/objects/:key',
  params: Key,
  query: RawQuery,
  response: z.union([ObjectDetail, RawObjectDetail]),
  summary: 'One object with counts, fields, relations and pipelines',
});

export const updateObjectContract = defineContract({
  method: 'PATCH',
  path: '/objects/:key',
  params: Key,
  body: UpdateObjectTypeInput,
  response: ObjectType,
  summary: 'Change labels, icon, colour, traits, app or title field of an object',
});

export const archiveObjectContract = defineContract({
  method: 'DELETE',
  path: '/objects/:key',
  params: Key,
  response: z.void(),
  summary:
    'Archive an object (records are kept; refused with the list while relations or expressions of other objects depend on it)',
});

// -- fields -----------------------------------------------------------------------------------------

export const listFieldsContract = defineContract({
  method: 'GET',
  path: '/objects/:key/fields',
  params: Key,
  query: RawQuery.extend({ includeArchived: q.bool().optional() }),
  response: z.union([z.array(Field), z.array(RawField)]),
  summary: 'Fields of an object in order (archived ones with includeArchived)',
});

export const createFieldContract = defineContract({
  method: 'POST',
  path: '/objects/:key/fields',
  params: Key,
  body: CreateFieldInput,
  status: 201,
  response: Field,
  summary: 'Create a field (relations: POST /relations; formulas are checked by the expression gate)',
});

export const reorderFieldsContract = defineContract({
  method: 'POST',
  path: '/objects/:key/fields/reorder',
  params: Key,
  body: ReorderInput,
  response: z.array(Field),
  summary: 'Set the order of the fields of an object',
});

export const getFieldContract = defineContract({
  method: 'GET',
  path: '/objects/:key/fields/:fieldKey',
  params: KeyField,
  query: RawQuery,
  response: z.union([Field, RawField]),
  summary: 'One field',
});

export const updateFieldContract = defineContract({
  method: 'PATCH',
  path: '/objects/:key/fields/:fieldKey',
  params: KeyField,
  body: UpdateFieldInput,
  response: Field,
  summary: 'Change a field (a key change rewrites every expression that reads it)',
});

export const archiveFieldContract = defineContract({
  method: 'DELETE',
  path: '/objects/:key/fields/:fieldKey',
  params: KeyField,
  query: z.object({ force: q.bool().optional() }),
  response: z.void(),
  summary:
    'Archive a field (values are kept); refused with the dependents (409 field_in_use) while expressions read it, unless force',
});

// -- relations & link fields ---------------------------------------------------------------------

export const listRelationsContract = defineContract({
  method: 'GET',
  path: '/relations',
  query: ObjectFilter,
  response: z.union([z.array(Relation), z.array(RawRelation)]),
  summary: 'Relations of the workspace (or of one object)',
});

export const createRelationContract = defineContract({
  method: 'POST',
  path: '/relations',
  body: CreateRelationInput,
  status: 201,
  response: Relation,
  summary: 'Create a relation and its fields on both objects',
});

export const getRelationContract = defineContract({
  method: 'GET',
  path: '/relations/:id',
  params: Id,
  query: RawQuery,
  response: z.union([Relation, RawRelation]),
  summary: 'One relation with its link fields',
});

export const updateRelationContract = defineContract({
  method: 'PATCH',
  path: '/relations/:id',
  params: Id,
  body: UpdateRelationInput,
  response: Relation,
  summary: 'Change the cardinality or delete behaviour of a relation',
});

export const deleteRelationContract = defineContract({
  method: 'DELETE',
  path: '/relations/:id',
  params: Id,
  response: z.void(),
  summary: 'Remove a relation (its fields are archived, links are kept)',
});

export const createLinkFieldContract = defineContract({
  method: 'POST',
  path: '/relations/:id/link-fields',
  params: Id,
  body: CreateLinkFieldInput,
  status: 201,
  response: LinkField,
  summary:
    'Add a field to the links of a relation (display hints; a computed one is checked against the other link fields)',
});

export const updateLinkFieldContract = defineContract({
  method: 'PATCH',
  path: '/relations/:id/link-fields/:linkFieldId',
  params: IdLinkField,
  body: UpdateLinkFieldInput,
  response: LinkField,
  summary: 'Change a link field (label, options, order, display hints, computed expression)',
});

export const deleteLinkFieldContract = defineContract({
  method: 'DELETE',
  path: '/relations/:id/link-fields/:linkFieldId',
  params: IdLinkField,
  response: z.void(),
  summary: 'Remove a link field (computed link fields that read it are refused first)',
});

// -- pipelines & stages ------------------------------------------------------------------------

export const listPipelinesContract = defineContract({
  method: 'GET',
  path: '/pipelines',
  query: ObjectFilter,
  response: z.union([z.array(Pipeline), z.array(RawPipeline)]),
  summary: 'Pipelines with their stages',
});

export const createPipelineContract = defineContract({
  method: 'POST',
  path: '/pipelines',
  body: CreatePipelineInput,
  status: 201,
  response: Pipeline,
  summary: 'Create a pipeline of a status field with its stages',
});

export const getPipelineContract = defineContract({
  method: 'GET',
  path: '/pipelines/:id',
  params: Id,
  query: RawQuery,
  response: z.union([Pipeline, RawPipeline]),
  summary: 'One pipeline with its stages',
});

export const updatePipelineContract = defineContract({
  method: 'PATCH',
  path: '/pipelines/:id',
  params: Id,
  body: UpdatePipelineInput,
  response: Pipeline,
  summary: 'Rename a pipeline, make it the default or move it',
});

export const deletePipelineContract = defineContract({
  method: 'DELETE',
  path: '/pipelines/:id',
  params: Id,
  response: z.void(),
  summary: 'Delete a pipeline without records',
});

export const createStageContract = defineContract({
  method: 'POST',
  path: '/pipelines/:id/stages',
  params: Id,
  body: CreateStageInput,
  status: 201,
  response: Stage,
  summary: 'Add a stage to a pipeline',
});

export const reorderStagesContract = defineContract({
  method: 'POST',
  path: '/pipelines/:id/stages/reorder',
  params: Id,
  body: ReorderInput,
  response: Pipeline,
  summary: 'Set the order of the stages of a pipeline',
});

export const updateStageContract = defineContract({
  method: 'PATCH',
  path: '/pipelines/:id/stages/:stageId',
  params: IdStage,
  body: UpdateStageInput,
  response: Stage,
  summary: 'Change a stage (requirements, entry condition, probability, group, colour …)',
});

export const deleteStageContract = defineContract({
  method: 'DELETE',
  path: '/pipelines/:id/stages/:stageId',
  params: IdStage,
  response: z.void(),
  summary: 'Delete a stage without records',
});

// -- option sets --------------------------------------------------------------------------------
// Reusable options of select and multi-select fields: a field names a set in `config.optionSet`.

const OptionSetKey = z.object({ key: z.string().min(1).max(63) });

export const listOptionSetsContract = defineContract({
  method: 'GET',
  path: '/option-sets',
  query: RawQuery,
  response: z.union([z.array(OptionSet), z.array(RawOptionSet)]),
  summary: 'Option sets shared by select and multi-select fields, with the fields that use them',
});

export const createOptionSetContract = defineContract({
  method: 'POST',
  path: '/option-sets',
  body: CreateOptionSetInput,
  status: 201,
  response: OptionSet,
  summary: 'Create an option set',
});

export const getOptionSetContract = defineContract({
  method: 'GET',
  path: '/option-sets/:key',
  params: OptionSetKey,
  query: RawQuery,
  response: z.union([OptionSet, RawOptionSet]),
  summary: 'One option set with the fields that use it',
});

export const updateOptionSetContract = defineContract({
  method: 'PATCH',
  path: '/option-sets/:key',
  params: OptionSetKey,
  body: UpdateOptionSetInput,
  response: OptionSet,
  summary: 'Rename an option set or replace its options (every field using it follows)',
});

export const deleteOptionSetContract = defineContract({
  method: 'DELETE',
  path: '/option-sets/:key',
  params: OptionSetKey,
  response: z.void(),
  summary: 'Delete an option set no field uses',
});
